<template>
    <v-container fluid class="stats-page">
        <!-- ---------- Header + filters ---------- -->
        <div class="stats-header">
            <div class="stats-header__top">
                <span class="stats-header__icon custom-orange">
                    <v-icon color="white">mdi-chart-box-outline</v-icon>
                </span>
                <h1 class="stats-header__title">Στατιστικά</h1>
                <v-btn-toggle v-model="option" mandatory class="stats-toggle">
                    <v-btn :value="0" small class="stats-toggle__btn">
                        <v-icon small left>mdi-account</v-icon>
                        Παίκτες
                    </v-btn>
                    <v-btn :value="1" small class="stats-toggle__btn">
                        <v-icon small left>mdi-account-group</v-icon>
                        Ομάδες
                    </v-btn>
                </v-btn-toggle>
            </div>
            <div class="stats-header__filters">
                <v-select
                    v-model="season"
                    color="orange"
                    item-color="orange"
                    label="Σεζόν"
                    :items="seasons"
                    item-text="name"
                    return-object
                    dense
                    outlined
                    hide-details
                    prepend-inner-icon="mdi-calendar"
                    class="stats-header__season"
                />
                <v-select
                    v-model="league"
                    color="orange"
                    item-color="orange"
                    :items="leagues"
                    label="Κατηγορία"
                    item-text="title"
                    return-object
                    item-value="id"
                    dense
                    outlined
                    hide-details
                    prepend-inner-icon="mdi-basketball"
                    class="stats-header__league"
                />
                <v-btn-toggle v-if="phaseOptions.length" v-model="phase" mandatory class="stats-toggle stats-toggle--phase">
                    <v-btn v-for="item in phaseOptions" :key="item.value" :value="item.value" small class="stats-toggle__btn">
                        {{ item.title }}
                    </v-btn>
                </v-btn-toggle>
            </div>
        </div>

        <!-- ---------- Leaders ---------- -->
        <div class="stats-section-title">
            <span class="stats-section-title__bar custom-orange" />
            {{ option === 0 ? 'Κορυφαίοι παίκτες' : 'Κορυφαίες ομάδες' }}
            <span class="stats-section-title__hint">μέσος όρος ανά αγώνα</span>
        </div>
        <v-row class="stats-leaders">
            <v-col v-for="statHeader in statsHeaders" :key="statHeader.value" cols="12" sm="6" md="4" lg="3">
                <stat-card :loading="loading" :stat-header="statHeader" :stats="getDisplayData(statHeader)" :option="option === 1" @scrollToStats="scrollToStats()" />
            </v-col>
        </v-row>

        <!-- ---------- Charts ---------- -->
        <template v-if="option === 0 && topPirPlayers.length">
            <div class="stats-section-title">
                <span class="stats-section-title__bar custom-orange" />
                Εξέλιξη PIR
            </div>
            <pir-progression-chart :players="topPirPlayers" />
        </template>
        <template v-if="option === 1">
            <div class="stats-section-title">
                <span class="stats-section-title__bar custom-orange" />
                Ανάλυση ομάδων
            </div>
            <offensive-rating-diagram :teams="stasData || []" :loading="loading" class="mb-5" />
            <volume-efficiency-diagram :teams="stasData || []" :loading="loading" />
        </template>

        <!-- ---------- Full table ---------- -->
        <div ref="stats" class="stats-section-title stats-section-title--table">
            <span class="stats-section-title__bar custom-orange" />
            Αναλυτικά στατιστικά
            <v-btn-toggle v-model="option2" mandatory class="stats-toggle stats-toggle--small">
                <v-btn :value="0" small class="stats-toggle__btn">Μέσοι όροι</v-btn>
                <v-btn :value="1" small class="stats-toggle__btn">Σύνολο</v-btn>
            </v-btn-toggle>
        </div>
        <players-stats v-if="!option" :players="playerStats" :option="option2" :generic="true" class="px-0" />
        <teams-stats v-else :teams="stasData" :option="option2" :generic="true" class="px-0" />
    </v-container>
</template>

<script>
    import StatCard from '@/components/StatCard'
    import statsService from '@/services/stats.js'
    import leagueService from '@/services/league.js'
    import PlayersStats from '@/components/PlayersStats'
    import TeamsStats from '@/components/TeamsStats'
    import seasonService from '@/services/season.js'
    import moment from 'moment'
    import { availablePhases, pickPhase } from '@/utils/phases'
    import OffensiveRatingDiagram from '@/components/OffensiveRatingDiagram'
    import VolumeEfficiencyDiagram from '@/components/VolumeEfficiencyDiagram'
    import PirProgressionChart from '@/components/PirProgressionChart'
    import { foulsOf } from '@/utils/playerStats'

    const PHASE_OPTIONS = [
        { title: 'Προκριματικά', value: 'placements' },
        { title: 'Regular Season', value: 'regular_season' },
        { title: 'Playoffs', value: 'playoffs' }
    ]

    export default {
        components: { StatCard, PlayersStats, TeamsStats, OffensiveRatingDiagram, VolumeEfficiencyDiagram, PirProgressionChart },
        data() {
            return {
                statsHeaders: [
                    { text: 'Pts', label: 'Πόντοι', icon: 'mdi-basketball', value: 'points' },
                    { text: '2P Made', label: 'Εύστοχα δίποντα', icon: 'mdi-numeric-2-circle-outline', value: 'twoPointsMade' },
                    { text: '3P Made', label: 'Εύστοχα τρίποντα', icon: 'mdi-numeric-3-circle-outline', value: 'threePointsMade' },
                    { text: 'FT Made', label: 'Εύστοχες βολές', icon: 'mdi-target', value: 'freeThrowsMade' },
                    { text: 'OR', label: 'Επιθετικά ριμπάουντ', icon: 'mdi-arrow-up-bold-circle-outline', value: 'offensiveRebounds' },
                    { text: 'DR', label: 'Αμυντικά ριμπάουντ', icon: 'mdi-shield-outline', value: 'defensiveRebounds' },
                    { text: 'TR', label: 'Ριμπάουντ', icon: 'mdi-basketball-hoop-outline', value: 'rebounds' },
                    { text: 'AST', label: 'Ασίστ', icon: 'mdi-handshake-outline', value: 'assists' },
                    { text: 'STL', label: 'Κλεψίματα', icon: 'mdi-hand-back-left-outline', value: 'steals' },
                    { text: 'TO', label: 'Λάθη', icon: 'mdi-alert-circle-outline', value: 'turnovers' },
                    { text: 'BLK', label: 'Κοψίματα', icon: 'mdi-cancel', value: 'blockedShots' },
                    { text: 'FC', label: 'Φάουλ', icon: 'mdi-whistle-outline', value: 'personalFouls' }
                ],
                stasData: null,
                playerStats: [],
                teamStats: [],
                leagues: [],
                league: null,
                option: 0,
                option2: 0,
                phase: 'regular_season',
                activePhasePresetApplied: false,
                season: null,
                seasons: [],
                // what the season has to show (null: unknown, all the options), and the season it is of
                seasonPhases: null,
                seasonPhasesId: null,
                loading: false,
                // the stats of the latest choice only
                statsRequest: 0,
                maxRound: 0
            }
        },
        computed: {
            activePhase() {
                const config = this.$store.getters.getConfig
                return config && config.activePhase ? config.activePhase : null
            },
            // only the phases of the league with a match played
            phaseOptions() {
                return availablePhases(PHASE_OPTIONS, this.seasonPhases, this.league, 'played')
            },
            topPirPlayers() {
                if (this.option !== 0) return []

                return (Array.isArray(this.playerStats) ? this.playerStats : [])
                    .filter(player => this.hasPirHistory(player) && this.pirEligibilityThreshold(player))
                    .map(player => {
                        const pirHistory = this.getPirHistory(player)
                        const averagePir = pirHistory.length
                            ? pirHistory.reduce((sum, value) => sum + value, 0) / pirHistory.length
                            : 0

                        return {
                            ...player,
                            averagePir,
                            pirHistory
                        }
                    })
                    .sort((a, b) => b.averagePir - a.averagePir)
                    .slice(0, 10)
            }
        },
        watch: {
            season: {
                handler(value, previous) {
                    // another season: nothing on screen until its stats come
                    if (previous) {
                        this.statsRequest++
                        this.leagues = []
                        this.league = null
                        this.stasData = null
                        this.playerStats = []
                        this.teamStats = []
                        this.maxRound = 0
                        this.loading = false
                        this.$store.dispatch('setLeagueFilter', null)
                    }
                    if (value) {
                        this.fetchSeasonPhases(value)
                        this.fetchAllLeagues()
                    }
                }
            },
            league: {
                handler() {
                    this.fetchStats()
                }
            },
            phase: {
                handler() {
                    this.fetchStats()
                }
            },
            activePhase() {
                this.applyPhasePreset()
            },
            phaseOptions() {
                this.fixPhase()
            }
        },
        created() {
            this.applyPhasePreset()
            this.fetchAllSeasons()
        },
        methods: {
            applyPhasePreset() {
                if (!this.activePhasePresetApplied && this.activePhase && (this.phase === null || this.phase === 'regular_season')) {
                    this.phase = this.activePhase
                    this.activePhasePresetApplied = true
                    return
                }

                if (!PHASE_OPTIONS.some(option => option.value === this.phase)) {
                    this.phase = 'regular_season'
                }
            },
            fetchSeasonPhases(season) {
                this.seasonPhasesId = null
                const done = (phases) => {
                    if (!this.season || this.season.id !== season.id) return
                    this.seasonPhases = phases
                    this.seasonPhasesId = season.id
                    this.fixPhase()
                    this.fetchStats()
                }
                seasonService.getPhases(season.id).then(resp => done(resp.data || null)).catch(() => done(null))
            },
            // a phase without a match played has no stats: not an option
            fixPhase() {
                this.phase = pickPhase(this.phase, this.phaseOptions, this.activePhase)
            },
            fetchAllSeasons() {
                seasonService.getSeasons(1, 100).then(resp => {
                    this.seasons = resp.data.content
                    this.season = resp.data.content.filter(element => moment().isBetween(moment(element.startDate), moment(element.endDate)))[0]
                })
            },
            getDisplayData(statHeader) {
                if (this.maxRound > 0) {
                    const temp = JSON.parse(JSON.stringify(this.option === 1 ? (this.stasData || []) : this.playerStats.filter((element) => {
                        return element.stats.length >= (this.maxRound * 0.50)
                    })))
                    temp.sort((a, b) => b.avg[statHeader.value] - a.avg[statHeader.value])
                    return temp.length > 5 ? temp.slice(0, 5) : temp
                } else {
                    return []
                }
            },
            fetchStats() {
                // the phases of the season first: the phase may not be an option
                if (this.season && this.league && this.seasonPhasesId === this.season.id) {
                    this.loading = true
                    this.$store.dispatch('setLeagueFilter', this.league)
                    this.maxRound = 0
                    const request = ++this.statsRequest
                    statsService.getStatsByLeague(this.league.id, this.phase, this.season.id).then(resp => {
                        if (request !== this.statsRequest) return
                        this.calculateTeamsStats(resp.data)
                        this.calculatePlayersStats(resp.data)
                        this.getPlayersStats()
                    }).finally(() => {
                        if (request === this.statsRequest) this.loading = false
                    })
                }
            },
            fetchAllLeagues() {
                // the leagues of the season in any phase: the phase is picked after the league
                const season = this.season
                leagueService.getLeagues(1, 100, season, null).then(resp => {
                    if (this.season !== season) return
                    this.leagues = resp.data.sort((a, b) => a.title.localeCompare(b.title))
                    this.league = this.$store.getters.getLeagueFilter ? this.$store.getters.getLeagueFilter : (this.leagues[0] || null)
                })
            },
            calculateTeamsStats(data) {
                data.forEach(team => {
                    this.teamStats = []
                    team.players.forEach(player => {
                        this.teamStats = this.teamStats.concat(player.stats)
                    })
                    team.stats = this.groupByMatch()
                    team.avg = this.getAverageStats(team)
                    team.cum = this.getCummulativeStats(team)
                    team.gp = team.stats.length
                    if (team.stats.length > this.maxRound) {
                        this.maxRound = team.stats.length
                    }
                })
                this.stasData = data
            },
            findMatches() {
                const matches = new Set()
                this.teamStats.forEach(stat => {
                    matches.add(stat.matchId)
                })
                return matches
            },
            groupByMatch() {
                const matches = this.findMatches()
                const data = []
                matches.forEach(match => {
                    const stats = {
                        points: 0,
                        rebounds: 0,
                        assists: 0,
                        assistPoints: 0,
                        secondChancePoints: 0,
                        pointsOffTurnovers: 0,
                        steals: 0,
                        turnovers: 0,
                        blockedShots: 0,
                        twoPointsMade: 0,
                        twoPointAttempts: 0,
                        threePointsMade: 0,
                        threePointAttempts: 0,
                        freeThrowsMade: 0,
                        freeThrowAttempts: 0,
                        offensiveRebounds: 0,
                        defensiveRebounds: 0,
                        personalFouls: 0,
                        fouls: 0
                    }
                    this.teamStats.forEach(stat => {
                        if (match === stat.matchId) {
                            for (const key of Object.keys(stats)) {
                                stats[key] += key === 'fouls' ? foulsOf(stat) : stat[key]
                            }
                        }
                    })
                    data.push(stats)
                })
                return data
            },
            getPlayersStats() {
                this.playerStats = []
                this.stasData.forEach(team => {
                    this.playerStats = this.playerStats.concat(team.players)
                })
                this.loading = false
                return this.playerStats
            },
            calculatePlayersStats(data) {
                data.forEach(team => {
                    team.players.forEach(player => {
                        player.team = team.name
                        player.teamLogo = team.logo
                        player.teamColor = team.color
                        player.avg = this.getAverageStats(player)
                        player.cum = this.getCummulativeStats(player)
                    })
                })
                this.stasData = data
            },
            getCummulativeStats(entity) {
                const stats = {
                    points: 0,
                    rebounds: 0,
                    assists: 0,
                    assistPoints: 0,
                    secondChancePoints: 0,
                    pointsOffTurnovers: 0,
                    steals: 0,
                    turnovers: 0,
                    blockedShots: 0,
                    twoPointsMade: 0,
                    twoPointAttempts: 0,
                    threePointsMade: 0,
                    threePointAttempts: 0,
                    freeThrowsMade: 0,
                    freeThrowAttempts: 0,
                    offensiveRebounds: 0,
                    defensiveRebounds: 0,
                    personalFouls: 0,
                    fouls: 0,
                    playTimeInSecs: 0
                }
                entity.stats.filter((element) => !element.cup).forEach(element => {
                    for (const key of Object.keys(stats)) {
                        stats[key] += key === 'fouls' ? foulsOf(element) : element[key]
                    }
                })
                return stats
            },
            getAverageStats(entity) {
                const stats = {
                    points: 0,
                    rebounds: 0,
                    assists: 0,
                    assistPoints: 0,
                    secondChancePoints: 0,
                    pointsOffTurnovers: 0,
                    steals: 0,
                    turnovers: 0,
                    blockedShots: 0,
                    twoPointsMade: 0,
                    twoPointAttempts: 0,
                    threePointsMade: 0,
                    threePointAttempts: 0,
                    freeThrowsMade: 0,
                    freeThrowAttempts: 0,
                    offensiveRebounds: 0,
                    defensiveRebounds: 0,
                    personalFouls: 0,
                    fouls: 0,
                    playTimeInSecs: 0
                }
                const temp = entity.stats.filter((element) => !element.cup)
                temp.forEach(element => {
                    for (const key of Object.keys(stats)) {
                        stats[key] += key === 'fouls' ? foulsOf(element) : element[key]
                    }
                })

                Object.keys(stats).forEach(element => {
                    if (temp.length) {
                        stats[element] = stats[element] / temp.length
                    } else {
                        stats[element] = 0
                    }
                })
                return stats
            },
            scrollToStats() {
                this.$refs.stats.scrollIntoView({ behavior: 'smooth' })
            },
            hasPirHistory(player = {}) {
                return this.getPirHistory(player).length > 0
            },
            pirEligibilityThreshold(player = {}) {
                const team = (Array.isArray(this.stasData) ? this.stasData : []).find(item => item.name === player.team)
                const teamMatches = Number(team?.gp) || (Array.isArray(team?.stats) ? team.stats.length : 0)
                if (!teamMatches) return false

                const playerMatches = this.getPirHistory(player).length
                return playerMatches >= Math.ceil(teamMatches * 0.5)
            },
            getPirHistory(player = {}) {
                const stats = Array.isArray(player.stats) ? player.stats.filter(stat => stat && !stat.cup) : []

                return stats
                    .slice()
                    .sort((a, b) => {
                        const aMatch = Number(a.matchId)
                        const bMatch = Number(b.matchId)
                        if (Number.isFinite(aMatch) && Number.isFinite(bMatch)) {
                            return aMatch - bMatch
                        }
                        return String(a.matchId || '').localeCompare(String(b.matchId || ''))
                    })
                    .map(stat => Number(stat.pir) || 0)
            }
        }
    }
</script>

<style scoped>
.stats-page {
    max-width: 1200px;
    margin: 0 auto;
    font-family: Ubuntu, Arial, sans-serif;
    text-align: left;
}

/* ---------- Header ---------- */
.stats-header {
    padding: 16px 20px;
    background: #fff;
    border-radius: 12px;
    border-top: 4px solid #ff7133;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
}

.stats-header__top {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 14px;
}

.stats-header__icon {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 10px;
}

.stats-header__title {
    flex: 1 1 auto;
    font-family: Mansalva, Arial, sans-serif;
    font-size: 1.9rem;
    font-weight: 800;
    line-height: 1.2;
}

.stats-header__filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 16px;
    margin-top: 16px;
}

.stats-header__season {
    flex: 0 0 330px;
}

.stats-header__league {
    flex: 1 1 240px;
    max-width: 360px;
}

/* ---------- Toggles (same look as the filters bar) ---------- */
.stats-toggle {
    background: #0d0d0f !important;
    border-radius: 8px !important;
    padding: 3px;
}

.stats-toggle .stats-toggle__btn {
    background: transparent !important;
    border: none !important;
    border-radius: 6px !important;
    color: rgba(255, 255, 255, 0.75) !important;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: none;
    height: 34px !important;
}

.stats-toggle .stats-toggle__btn .v-icon {
    color: inherit !important;
}

.stats-toggle .stats-toggle__btn.v-btn--active {
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 50%, orange 100%) !important;
    color: #fff !important;
}

.stats-toggle .stats-toggle__btn.v-btn--active::before {
    opacity: 0 !important;
}

.stats-toggle--small .stats-toggle__btn {
    height: 28px !important;
    font-size: 0.75rem;
}

/* ---------- Section titles ---------- */
.stats-section-title {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin: 28px 0 12px;
    padding: 8px 14px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    font-size: 1.1rem;
    font-weight: 800;
}

.stats-section-title__bar {
    width: 5px;
    height: 20px;
    border-radius: 2px;
}

.stats-section-title__hint {
    color: rgba(0, 0, 0, 0.45);
    font-size: 0.8rem;
    font-weight: 600;
}

.stats-section-title--table .stats-toggle {
    margin-left: auto;
}

.stats-leaders {
    margin-top: -4px;
}

@media (max-width: 600px) {
    .stats-header {
        padding: 14px;
    }

    .stats-header__title {
        font-size: 1.45rem;
    }

    .stats-header__season,
    .stats-header__league {
        flex: 1 1 100%;
        max-width: none;
    }

    .stats-toggle--phase {
        display: flex;
        width: 100%;
    }

    .stats-toggle--phase .stats-toggle__btn {
        flex: 1 1 0;
        min-width: 0 !important;
        font-size: 0.75rem;
        padding: 0 4px !important;
    }

    .stats-section-title--table .stats-toggle {
        margin-left: 0;
    }
}
</style>
